<template>
  <div :class="{ dark: isDarkMode }">
    <!-- Mobile Header Bar -->
    <v-app-bar
      fixed
      dense
      class="flex justify-between items-center"
      :color="$vuetify.theme.currentTheme.appBarColor"
      app
    >
      <div class="d-flex align-center">
        <v-btn icon color="white" @click="mobileMenuOpen = true" class="mr-2">
          <v-icon>mdi-menu</v-icon>
        </v-btn>
        <div class="image-container flex items-center" v-if="orgData">
          <img
            class="image-container ml-0 cursor-pointer"
            v-if="orgData.logo"
            :src="getLogoImage"
            @click="goToDashboard"
            style="max-height: 32px; width: auto"
          />
          <img
            v-else
            class="image-container ml-0 cursor-pointer"
            src="@/assets/img/CW_DEFAULT.png"
            @click="goToDashboard"
            style="max-height: 32px; width: auto"
          />
        </div>
        <v-skeleton-loader v-else height="32" type="card" width="70"></v-skeleton-loader>
      </div>

      <div class="flex justify-end items-center shrink-0">
        <!-- Notification Bell -->
        <div class="notification mr-2">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" v-show="isbellIconEnabled('NOTIFICATIONS')">
                <v-badge color="red" overlap :content="alertsCount" v-if="alertsCount">
                  <v-icon class="white--text">mdi-bell</v-icon>
                </v-badge>
                <v-icon class="white--text" v-else>mdi-bell</v-icon>
              </div>
            </template>

            <v-card class="w-[20rem] max-h-[30rem]">
              <v-list class="mt-2 w-full" dense>
                <v-list-item v-for="(notification, index) in alerts.slice(0, 4)" :key="index">
                  <v-alert
                    :type="notification.type"
                    class="w-full p-1 text-sm mb-1"
                    :color="categoryColor(notification.notificationGenric)?.bgColor"
                    text
                    outlined
                  >
                    {{ notification.message }}
                  </v-alert>
                </v-list-item>
                <v-list-item v-if="!alerts.length">
                  <v-alert type="warning" color="blue" class="w-full p-1 text-sm">
                    No alerts
                  </v-alert>
                </v-list-item>
                <v-list-item
                  @click="handleSeeAllMobile"
                  class="cursor-pointer flex justify-center hover:bg-slate-200 hover:dark-icon-bg dark-text-color mx-4 rounded"
                >
                  See all &nbsp;
                  <span v-if="alertsCount > 4"> (+{{ alertsCount - 4 }} more)</span>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </div>

        <!-- User Quick Avatar -->
        <!-- <div class="user-menu">
          <v-avatar size="32" class="cursor-pointer border border-white" @click="mobileMenuOpen = true">
            <img
              v-if="user?.user?.profile_image"
              :src="getEmployeeImage()"
              alt="Profile"
            />
            <v-icon v-else color="white">mdi-account</v-icon>
          </v-avatar>
        </div> -->
      </div>
    </v-app-bar>

    <!-- Full-Screen Navigation Overlay Drawer -->
    <v-dialog
      v-model="mobileMenuOpen"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="mobile-nav-card flex flex-col h-full">
        <!-- Overlay Header -->
        <v-toolbar flat dense dark color="#21427d" class="shrink-0 px-2">
          <v-toolbar-title class="font-bold text-white flex items-center">
            <v-avatar size="30" class="mr-2" tile>
              <img v-if="orgData && orgData.logo" :src="getLogoImage" />
              <v-icon v-else color="white">mdi-apps</v-icon>
            </v-avatar>
            {{ title || 'Admin Panel' }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="mobileMenuOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="flex-grow overflow-y-auto px-4 py-4">
          <!-- User Profile Section -->
          <div
            class="profile-card p-4 mb-4 rounded-lg bg-gray-50 flex items-center justify-between border"
          >
            <div class="flex items-center gap-3">
              <v-avatar size="48" class="border">
                <img
                  v-if="user?.user?.profile_image"
                  :src="getEmployeeImage()"
                  alt="User Profile"
                />
                <v-icon v-else size="28" color="grey darken-2">mdi-account</v-icon>
              </v-avatar>
              <div>
                <h4 class="text-base font-bold text-gray-800 capitalize mb-0">
                  Hi, {{ user_name }}
                </h4>
                <p class="text-xs text-gray-500 mb-0">{{ user?.user?.email || '' }}</p>
              </div>
            </div>
            <v-btn
              small
              outlined
              color="#21427d"
              class="rounded-lg normal-case"
              to="/workforce/profile"
              @click="mobileMenuOpen = false"
            >
              View Profile
            </v-btn>
          </div>

          <!-- Global Search -->
          <div class="mb-4" v-if="showGlobalSearch">
            <GlobalSearch :enableFetch="allowEnableFetch" class="w-full" />
          </div>

          <!-- Navigation Links -->
          <v-list class="mobile-menu-list mb-6" dense>
            <template v-for="(item, i) in menuItems">
              <template v-if="item.visible">
                <!-- Simple Link -->
                <v-list-item
                  v-if="item.to"
                  :key="i"
                  :to="item.to"
                  router
                  exact
                  active-class="mobile-item-active"
                  class="mb-2 rounded-lg border"
                  @click="mobileMenuOpen = false"
                >
                  <v-list-item-icon class="mr-3 my-2">
                    <v-icon size="20" color="#21427d">{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-sm font-semibold text-[#21427d]">
                      {{ item.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Grouped Child Routes -->
                <v-list-group
                  v-else-if="item.childRoutes"
                  :key="'group-' + i"
                  class="mb-2 rounded-lg border overflow-hidden"
                  active-class="mobile-group-active"
                  no-action
                >
                  <template v-slot:activator>
                    <v-list-item-icon class="mr-3 my-2">
                      <v-icon size="20" color="#21427d">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-sm font-semibold text-[#21427d]">
                        {{ item.title }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>

                  <v-list-item
                    v-for="(child, j) in item.childRoutes"
                    :key="child.id || j"
                    :to="child.to"
                    router
                    exact
                    active-class="mobile-child-item-active"
                    class="pl-8 py-2 bg-gray-50 border-t"
                    @click="mobileMenuOpen = false"
                  >
                    <v-list-item-icon class="mr-3 my-1">
                      <v-icon size="18" color="#21427d">{{ child.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-xs font-semibold text-[#21427d]">
                        {{ child.title }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-group>
              </template>
            </template>
          </v-list>

          <!-- Global Search and Quick Actions -->
          <v-divider class="mb-4"></v-divider>

          <div class="flex flex-col gap-3">
            <!-- Send Notification Dialog Action -->
            <v-btn
              color="#21427d"
              outlined
              block
              class="font-bold normal-case rounded-lg py-5"
              @click="triggerNotificationDialog"
              v-iam-disabled="`global.notifications.write`"
            >
              <v-icon left small>mdi-bell-plus-outline</v-icon>
              Send Notification
            </v-btn>

            <!-- App Download Link -->
            <v-btn
              block
              outlined
              color="green darken-2"
              class="font-bold normal-case rounded-lg py-5"
              @click="downloadApp"
            >
              <v-icon left small>mdi-android</v-icon>
              Download Employee App
            </v-btn>
          </div>
        </v-card-text>

        <!-- Drawer Footer -->
        <v-card-actions class="bg-gray-100 p-4 shrink-0 flex justify-between items-center border-t">
          <v-btn text small color="grey darken-2" @click="openInNewTab"> About Product </v-btn>
          <v-btn
            depressed
            color="red"
            class="white--text font-bold normal-case rounded-lg"
            @click="logout()"
          >
            <v-icon left size="16">mdi-logout</v-icon>
            Logout
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import GlobalSearch from '@/components/GlobalSearch.vue'

export default {
  name: 'DefaultLayout_mobile',
  components: {
    GlobalSearch
  },
  inject: ['layout'],
  data() {
    return {
      mobileMenuOpen: false
    }
  },
  computed: {
    isDarkMode() {
      return this.layout.isDarkMode
    },
    orgData() {
      return this.layout.orgData
    },
    getLogoImage() {
      return this.layout.getLogoImage
    },
    alertsCount() {
      return this.layout.alertsCount
    },
    alerts() {
      return this.layout.alerts
    },
    user() {
      return this.layout.user
    },
    user_name() {
      return this.layout.user_name
    },
    title() {
      return this.layout.title
    },
    menuItems() {
      return this.layout.menuItems
    },
    showGlobalSearch() {
      return this.layout.showGlobalSearch
    },
    allowEnableFetch() {
      return this.layout.allowEnableFetch
    },
    isRouteAllowed() {
      return this.layout.isRouteAllowed
    }
  },
  methods: {
    goToDashboard() {
      this.layout.goToDashboard()
    },
    isbellIconEnabled(moduleName) {
      return this.layout.isbellIconEnabled(moduleName)
    },
    categoryColor(generic) {
      return this.layout.categoryColor(generic)
    },
    getEmployeeImage() {
      return this.layout.getEmployeeImage()
    },
    downloadApp() {
      this.layout.downloadApp()
    },
    openInNewTab() {
      this.layout.openInNewTab()
    },
    logout() {
      this.layout.logout()
    },
    handleSeeAllMobile() {
      this.mobileMenuOpen = false
      this.layout.handleSeeAll()
    },
    triggerNotificationDialog() {
      this.mobileMenuOpen = false
      this.layout.showNotificationDialog = true
    }
  }
}
</script>

<style scoped>
.mobile-nav-card {
  background-color: #ffffff;
}

.mobile-menu-list .v-list-item {
  transition: all 0.2s ease-in-out;
}

.mobile-item-active {
  background-color: rgba(33, 66, 125, 0.08) !important;
  border-color: #21427d !important;
}

.mobile-group-active {
  border-color: #21427d !important;
}

.mobile-child-item-active {
  background-color: rgba(33, 66, 125, 0.05) !important;
}
</style>
